<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useRemoveLuggage } from '../model/useRemoveLuggage';
import { BaggageQrScannerModal } from '@/features/baggageQrScan';
import { ErrorSnackbar } from '@/shared/ui';

export type Props = {
    /** モーダルの開閉状態 */
    modelValue: boolean;
    /** 申し込みID */
    applicationId?: string;
    /** 現在の荷物QRコード一覧 */
    currentLuggageQrCodes?: readonly string[];
};

const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    applicationId: '',
    currentLuggageQrCodes: () => [],
});

const emit = defineEmits<{
    'update:modelValue': [boolean];
    'luggage-removed': [];
}>();

// ローカル状態管理
const isOpen = ref(props.modelValue);
const showQrScanner = ref(false);
const selectedQrCode = ref('');

// カスタムフック
const { isLoading, errorMessage, successMessage, removeLuggageFromApplication, clearMessages } = useRemoveLuggage();
const showQrError = ref(false);

// 現在の荷物リストが空かどうか
const hasLuggage = computed(() => props.currentLuggageQrCodes.length > 0);

// modelValueの変更を監視
watch(
    () => props.modelValue,
    (newValue) => {
        isOpen.value = newValue;
        if (newValue) {
            resetForm();
        }
    }
);

// isOpenの変更を親に通知
watch(isOpen, (newValue) => {
    emit('update:modelValue', newValue);
});

// フォームリセット
const resetForm = () => {
    selectedQrCode.value = '';
    clearMessages();
    showQrScanner.value = false;
};

// モーダルを閉じる
const closeModal = () => {
    isOpen.value = false;
    resetForm();
};

// QRスキャナーを開く
const openQrScanner = () => {
    showQrScanner.value = true;
};

// QRコードスキャン成功時
const handleQrDetect = (qrCode: string) => {
    if (props.currentLuggageQrCodes.includes(qrCode)) {
        selectedQrCode.value = qrCode;
        showQrScanner.value = false;
    } else {
        showQrError.value = true;
    }
};

// リストからQRコードを選択
const selectFromList = (qrCode: string) => {
    selectedQrCode.value = qrCode;
};

// 選択をクリア
const clearSelection = () => {
    selectedQrCode.value = '';
};

// 荷物削除実行
const handleRemoveLuggage = async () => {
    if (!props.applicationId || !selectedQrCode.value) {
        return;
    }

    const success = await removeLuggageFromApplication(props.applicationId, selectedQrCode.value);

    if (success) {
        emit('luggage-removed');
        // 少し遅らせてモーダルを閉じる（成功メッセージを見せるため）
        setTimeout(() => {
            closeModal();
        }, 1500);
    }
};

// バリデーション
const isValidForm = () => {
    return selectedQrCode.value && props.applicationId;
};
</script>

<template>
    <v-dialog v-model="isOpen" max-width="700" persistent>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center bg-error text-white">
                <div class="d-flex align-center">
                    <v-icon class="me-2">mdi-package-minus</v-icon>
                    <span>荷物削除</span>
                </div>
                <v-btn icon="mdi-close" variant="text" color="white" size="small" @click="closeModal" />
            </v-card-title>

            <v-card-text class="pa-6">
                <!-- 荷物がない場合の表示 -->
                <div v-if="!hasLuggage" class="text-center py-8">
                    <v-icon size="64" color="grey-lighten-1" class="mb-2">mdi-package-variant-remove</v-icon>
                    <p class="text-h6 text-grey mb-2">削除可能な荷物がありません</p>
                    <p class="text-body-2 text-grey">まず荷物を追加してから削除操作を行ってください。</p>
                </div>

                <!-- 荷物がある場合の削除フォーム -->
                <div v-else>
                    <!-- 成功メッセージ -->
                    <v-alert
                        v-if="successMessage"
                        type="success"
                        variant="outlined"
                        class="mb-4"
                        :text="successMessage"
                    />

                    <!-- エラーメッセージ -->
                    <v-alert v-if="errorMessage" type="error" variant="outlined" class="mb-4" :text="errorMessage" />

                    <!-- 警告 -->
                    <v-alert type="warning" variant="outlined" class="mb-4">
                        <v-alert-title>注意</v-alert-title>
                        削除された荷物は申し込みから完全に除外されます。この操作は元に戻すことができません。
                    </v-alert>

                    <!-- 現在の荷物一覧 -->
                    <div class="mb-6">
                        <h4 class="text-subtitle-1 mb-3 d-flex align-center">
                            <v-icon class="me-2" color="primary">mdi-format-list-bulleted</v-icon>
                            現在の荷物一覧（{{ currentLuggageQrCodes.length }}個）
                        </h4>
                        <div class="luggage-list">
                            <v-card
                                v-for="(qrCode, index) in currentLuggageQrCodes"
                                :key="index"
                                variant="outlined"
                                :color="selectedQrCode === qrCode ? 'error' : 'default'"
                                class="luggage-item mb-2"
                                @click="selectFromList(qrCode)"
                                :class="{ 'selected-item': selectedQrCode === qrCode }"
                            >
                                <v-card-text class="pa-3 d-flex align-center justify-space-between">
                                    <div class="d-flex align-center">
                                        <v-icon class="me-2" color="primary">mdi-qrcode</v-icon>
                                        <span class="font-weight-medium">{{ qrCode }}</span>
                                    </div>
                                    <v-radio
                                        :model-value="selectedQrCode === qrCode"
                                        :value="true"
                                        color="error"
                                        hide-details
                                    />
                                </v-card-text>
                            </v-card>
                        </div>
                    </div>

                    <!-- その他の削除方法 -->
                    <v-divider class="my-4" />
                    <h4 class="text-subtitle-1 mb-3">その他の方法で削除</h4>

                    <v-btn
                        color="secondary"
                        variant="outlined"
                        prepend-icon="mdi-qrcode-scan"
                        @click="openQrScanner"
                        :disabled="isLoading"
                    >
                        QRコードスキャン
                    </v-btn>

                    <!-- 選択されたQRコード -->
                    <div v-if="selectedQrCode" class="mt-4">
                        <h4 class="text-subtitle-1 mb-2">削除対象</h4>
                        <v-chip size="large" color="error" variant="outlined" closable @click:close="clearSelection">
                            <v-icon start>mdi-qrcode</v-icon>
                            {{ selectedQrCode }}
                        </v-chip>
                    </div>
                </div>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0 ga-3">
                <v-spacer />
                <v-btn color="grey" variant="outlined" class="modal-btn" @click="closeModal" :disabled="isLoading">
                    キャンセル
                </v-btn>
                <v-btn
                    v-if="hasLuggage"
                    color="error"
                    class="modal-btn"
                    :disabled="!isValidForm() || isLoading"
                    :loading="isLoading"
                    @click="handleRemoveLuggage"
                >
                    荷物を削除
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- QRスキャナーモーダル -->
        <BaggageQrScannerModal v-model:is-open="showQrScanner" :scanned-items="[]" @detect="handleQrDetect" />

        <ErrorSnackbar v-model="showQrError"> 該当するQRコードがありません </ErrorSnackbar>
    </v-dialog>
</template>

<style scoped>
.luggage-list {
    max-height: 300px;
    overflow-y: auto;
}

.luggage-item {
    cursor: pointer;
    transition: all 0.2s ease;
}

.luggage-item:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.selected-item {
    background-color: rgba(244, 67, 54, 0.1);
    border-color: #f44336 !important;
}

.modal-btn {
    min-height: 44px;
    padding: 0 20px;
}
</style>
